import React, { Children, useEffect, useMemo, useRef, useState } from 'react'
import { RecipeFormProps } from './RecipeForm.types'
import { Avatar, Button, FormInput, NumberInput, RadioList, TextInput } from '../atoms'
import { Cpu, ExclamationTriangle, ToggleOff, ToggleOn, GearWideConnected } from 'react-bootstrap-icons'
import ComboFileSelector from '../ComboFileSelector/ComboFileSelector'
import ComboFolderSelector from '../ComboFolderSelector/ComboFolderSelector'
import { getHost, usePollinationPanel, ProjectJobInfo } from '../hooks'
import { useForm, Controller } from 'react-hook-form'
import { ajvResolver } from '@hookform/resolvers/ajv'
import { generateSchema } from './Utils'
import * as Switch from '@radix-ui/react-switch'

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'

import './RecipeForm.scss'
import ConfigureLocalRun, { _defaultConfig } from '../ConfigureLocalRun/ConfigureLocalRun'
import { Toaster, toast } from 'react-hot-toast'
import { SettingOutlined } from '@ant-design/icons'
import Tag from '../atoms/Tag/Tag'
import { isEqual } from 'lodash'

const host = getHost()

enum InputType {
  DAGGenericInput='DAGGenericInput',
  DAGStringInput='DAGStringInput', // mapped
  DAGIntegerInput='DAGIntegerInput', // mapped
  DAGNumberInput='DAGNumberInput', // mapped
  DAGBooleanInput='DAGBooleanInput',
  DAGFolderInput='DAGFolderInput', // mapped
  DAGFileInput='DAGFileInput', // mapped
  DAGPathInput='DAGPathInput',
  DAGArrayInput='DAGArrayInput',
  DAGJSONObjectInput='DAGJSONObjectInput'
}

enum InputTypeAlias {
  DAGGenericInputAlias='DAGGenericInputAlias',
  DAGStringInputAlias='DAGStringInputAlias', // mapped
  DAGIntegerInputAlias='DAGIntegerInputAlias', // mapped
  DAGNumberInputAlias='DAGNumberInputAlias', // mapped
  DAGBooleanInputAlias='DAGBooleanInputAlias',
  DAGFolderInputAlias='DAGFolderInputAlias', // mapped
  DAGFileInputAlias='DAGFileInputAlias', // mapped
  DAGPathInputAlias='DAGPathInputAlias',
  DAGArrayInputAlias='DAGArrayInputAlias',
  DAGJSONObjectInputAlias='DAGJSONObjectInputAlias',
  DAGLinkedInputAlias='DAGLinkedInputAlias'
}

interface IInputMapping {
  type: InputType | InputTypeAlias
  required: boolean
  name: string
  default: any | null | undefined
  spec: object| any | null | undefined
  description: string | undefined
  extensions: string[] | undefined
  customTab: string | undefined
  index: number
  hasAlias: InputTypeAlias | undefined
  useAlias: boolean
}

export const RecipeForm: React.FC<RecipeFormProps> = ({
  recipe,
  projectName,
  projectOwner,
  client,
  onSubmit,
  style = {},
  initAsLocal = false,
  controlledValues = {},
  submitRef = undefined,
  studyName = undefined,
  showForm = true,
  children = undefined
}) => {
  const { getJob, simulationPath, cpuCount } = usePollinationPanel()
  const [loading, setLoading] = useState(false)

  /**
   * Settings icon
   */
  const [displaySettings, setDisplaySettings] = useState(false)
  const [overSettings, setOverSettings] = useState(false)

  /**
   * Local study
   */
  const [handlers, setHandlers] = useState(true)

  const [localConfig, setLocalConfig] = useState({ 
    ..._defaultConfig, 
    isLocalJob: initAsLocal,
    localCPUCount: cpuCount,
    cloudProjectName: projectName, 
    cloudProjectOwner: projectOwner,
    localRunFolder: {
      pollination_local_path: simulationPath
    },
    useHandlers: handlers
  })

  const [triggers, setTriggers] = useState<boolean[]>(
    new Array(recipe.inputs.length).fill(handlers)
  )

  /**
   * Input mapping
   */
  const inputs = useMemo(() => {
    if (!recipe) return
    const data = recipe.inputs.map((i, index) => {
      const customTab = i?.annotations['group_identifier'] ?? undefined
      const alias = handlers && i?.alias?.find(a => a.platform.includes(host))

      const controlledValue = (controlledValues 
        && controlledValues[i.name]) && controlledValues[i.name].value
      
      const inputInfo = {
        type: InputType[i.type], 
        required: i.required,
        name: i.name,
        default: controlledValue ?? i['default'],
        spec: i.spec,
        description: i.description,
        extensions: i.hasOwnProperty('extensions') ?
          i['extensions'] : [],
        customTab: customTab,
        index: index,
        hasAlias: handlers && alias?.type,
        useAlias: handlers && triggers[index]
        } as IInputMapping

        alias && inputInfo.useAlias && Object.keys(alias).forEach((key) => {
          if (key in inputInfo) {
            // if alias is DAGGenericInputAlias keep native one
            // if alias is file and input is file keep native one
            // important for red messages if delete value 
            if (key == 'type') {
              if (alias[key] === 'DAGGenericInputAlias') {
                return
              } else if (alias[key] === 'DAGFileInputAlias'
                && inputInfo.type === 'DAGFileInput') {
                return
              }
            }

            // if Linked Input keep the native name
            // Example, see mapping: from sim-par and sim_par
            if (key == 'name' 
              && alias['type'] === 'DAGLinkedInputAlias') {
              return
            }

            if (key == 'default' 
              && controlledValue) {
              return
            }

            inputInfo[key] = alias[key]
          }
        })
        
        return inputInfo
      })

    return data
  }, [recipe, handlers, triggers, controlledValues])

  /**
   * https://stackoverflow.com/questions/31683075/how-to-do-a-deep-comparison-between-2-objects-with-lodash
   */
  const getObjectDiff = (obj1: any, obj2: any) => {
    const diff = Object.keys(obj1).reduce((result, key, name) => {
        if (!obj2.hasOwnProperty(key)) {
            result.push(key)
        } else if (isEqual(obj1[key], obj2[key])) {
            const resultKeyIndex = result.indexOf(key)
            result.splice(resultKeyIndex, 1)
        }
        return result
    }, Object.keys(obj2))

    return diff
  }

  const refInputs = useRef<IInputMapping[]>(inputs)

  useEffect(() => {
    if (!inputs) return
    // If form is not active set inputs
    if (!showForm) {
      register('study-name', { value: studyName ?? `${recipe.metadata.name} study` })
      inputs.forEach(i => setControlledInputs(i))
    }
    // Refresh controlled values
    if (refInputs.current) {
      const values = getObjectDiff(inputs, refInputs.current)
      values.forEach(_ => {
        handleReset(inputs.at(Number(_)))
      })
    }
    refInputs.current = inputs
  }, [inputs, showForm])

  /**
   * LOCAL ONLY - Create a copy of the recipe and remove alias
   */
  const userRecipe = useMemo(() => {
    if (!handlers) return undefined
    const copy = JSON.parse(JSON.stringify(recipe))
    copy.inputs.forEach((item, index) => {
      if (item?.alias && !triggers[index]) {
         item.alias = []
      }
    })
    return copy
  }, [triggers])

  const schema = generateSchema(inputs)

  const customTabs = useMemo(() => {
    if (!inputs) return

    return inputs
      .filter(i => i.customTab)
      .map(i => i.customTab)
      .filter((value, index, array) => array.indexOf(value) === index)
  }, [inputs])

  const hasCustomTabs = useMemo(() => {
    if (!customTabs) return
    return customTabs.length > 0
  }, [customTabs])

  /**
   * Alias trigger
   */
  const updateTrigger = (i: number) => {
    const updatedTriggers = triggers.map((item, index) => {
      if (i === index) item = !item
      return item
    })
    
    setTriggers(updatedTriggers)
  }

  /**
   * Hooks
   */
  const { 
    control, 
    register, 
    handleSubmit, 
    resetField,
    watch,
    setValue: formSetValue,
    clearErrors,
    setError,
    formState: { errors, isValid } 
  } = useForm({
    resolver: ajvResolver(schema),
    mode: 'onChange'
  })

  const watchStudyName = watch('study-name')

  /**
   * Form actions
   * @param data Output data
   */
  const _onSubmit = data => {
    // console.log(data)
    setLoading(true)

    // Get study name and description
    // Remove them from inputs
    const studyName = data['study-name']
    delete data['study-name']

    const studyDescription = data['study-description']
    delete data['study-description']

    // Delete undefined values
    Object.entries(data).forEach(([key, val]) => {
      if (!val) delete data[key]
    })

    // Update useHandlers attribute
    // TODO: Fix ConfigureLocalRun to read project slug changes 
    onSubmit(studyName, data, studyDescription, 
      { ...localConfig, 
        cloudProjectName: projectName, 
        cloudProjectOwner: projectOwner, 
        useHandlers: handlers }, 
      userRecipe)
    .finally(() => {
      setLoading(false)
    })
  }
  const onErrors = err => {
    console.log(err)
    console.log(errors)
  }

  const handleReset = (i: IInputMapping | any) => {
    resetField(i.name, {
      defaultValue: i.default
    })
  }

  /**
   * From JSON info to UI parts
   * @param i IIinputMapping object
   * @returns ReactNode
   */
  const formComponents = (i: IInputMapping) => { 
    // DAGLinkedInputAlias add placeholder 
    if (i.type === InputTypeAlias.DAGLinkedInputAlias) {
      formSetValue(i.name, 'linked', { 
        shouldValidate: false
      })
      return <Tag>Linked data</Tag>
    }
    
    switch (i.type) {
    case InputType.DAGNumberInput:
    case InputTypeAlias.DAGNumberInputAlias:
      return <NumberInput
          inputProps={ {
            ...register(i.name, { valueAsNumber: true }),
            required: i.required,
            defaultValue: i.default,
            min : i.spec?.minimum,
            max : i.spec?.maximum,
            step : (i.spec?.minimum && Math.abs(i.spec?.minimum) < 0.1) 
              ? Math.abs(i.spec?.minimum) : 
              0.1
          } }
          key = {i.name} 
          reset={() => handleReset(i)} />
      
      case InputType.DAGIntegerInput: 
      case InputTypeAlias.DAGIntegerInputAlias: 
        return <NumberInput key={i.name}
          icon={i.name.startsWith('cpu') 
            ? <Cpu />
            : undefined}
          inputProps= { {
            ...register(i.name, { valueAsNumber: true }),
            required: i.required,
            defaultValue: i.default,
            min : i.spec?.minimum,
            max : i.spec?.maximum,
            step : 1,
          } } 
          reset={() => handleReset(i)} />
      
      case InputType.DAGStringInput: 
      case InputTypeAlias.DAGStringInputAlias: 
        return i.spec?.enum 
          ? <Controller
            name={i.name}
            control={control}
            defaultValue={i.default}
            render={({
              field: { 
                onChange,
                value, 
                ...field
              }
            }) => <RadioList 
              items={i.spec.enum}
              name={i.name} 
              onChange={onChange}
              value={value} />} />
        : <TextInput key={i.name}
          inputProps={{
            ...register(i.name, { required: i.required }),
            defaultValue: i.default
          }} 
          reset={() => handleReset(i)} />
  
      case InputType.DAGFileInput:
      case InputTypeAlias.DAGFileInputAlias: 
        return <Controller
          name={i.name}
          control={control}
          defaultValue={i.default}
          render={({
            field: { 
              value, 
              onChange, 
              ...field 
            }
          }) => <ComboFileSelector
          projectOwner={projectOwner}
          projectName={projectName}
          defaultOption={i.extensions &&
            i.extensions?.includes('hbjson') &&
            host !== 'web' ? 'get_model' : 'get_cloud'}
          optionsConfig={{
            get_model: host !== 'web' && i.extensions && i.extensions.includes('hbjson'),
            get_local: host !== 'web',
            get_file: host === 'web'
          }}
          value={value}
          hidden={i.default != null} // Hide options if controlled
          onChange={onChange}
          inputProps={{
              ...field,
              accept: i.extensions && i.extensions?.map(e => `.${e}`).join(', '),
              defaultValue: i.default
            }}
            client={client} />} 
          />
      
      // TODO: Add it to version without input form
      case InputType.DAGFolderInput:
      case InputTypeAlias.DAGFolderInputAlias:
        return <Controller
          name={i.name}
          control={control}
          defaultValue={i.default}
          render={({
            field: { 
              value, 
              onChange, 
              ...field 
            }
          }) => <ComboFolderSelector
            projectOwner={projectOwner}
            projectName={projectName}
            value={value}
            hidden={i.default != null} // Hide options if controlled
            onChange={onChange}
            inputProps={{
              ...field,
              defaultValue: i.default
            }}
            client={client} />}
          />
      // Others...
      default:
        return
    }
  }

  const setControlledInputs = (i: IInputMapping) => { 
    // DAGLinkedInputAlias add placeholder 
    if (i.type === InputTypeAlias.DAGLinkedInputAlias) {
      register(i.name, { value: 'linked' }) 
      return
    }

    // Check required fields and show error message
    if (i.required && !i.default) {
      setError(i.name, { type: 'custom', message: `'${i.name}' is a required input.` })
      return
    }

    switch (i.type) {
    case InputType.DAGNumberInput:
    case InputTypeAlias.DAGNumberInputAlias:
      register(i.name, { 
        value: i.default,
        valueAsNumber: true,
        required: i.required,
        min: i.spec?.minimum,
        max: i.spec?.maximum })
        break
      case InputType.DAGIntegerInput: 
      case InputTypeAlias.DAGIntegerInputAlias: 
        register(i.name, { 
          valueAsNumber: true,
          value: i.default, 
          required: i.required,
          min: i.spec?.minimum,
          max: i.spec?.maximum })
          break
      case InputType.DAGStringInput: 
      case InputTypeAlias.DAGStringInputAlias: 
        register(i.name, { 
          value: i.default,
          required: i.required })
          break
      case InputType.DAGFileInput:
      case InputTypeAlias.DAGFileInputAlias: 
        register(i.name, { 
          value: i.default,
          required: i.required })
          break
      // Others...
      default:
        break
    }
  }

  /**
   * Warning for local study folder
   */
  const localStudyWarning = useMemo(() => {
    if (host === 'web' || !localConfig.isLocalJob 
      || !localConfig.localRunFolder || !watchStudyName) return

    const jobs: ProjectJobInfo[] = getJob(`${projectOwner}/${projectName}`) 
    const studyName = watchStudyName
      .replace(/[^a-zA-Z0-9]/g, '')
      .replace(/[ ()]/g, '')

    const folderCheck = `${localConfig.localRunFolder}\\${studyName}`

    const status = jobs
      .filter(j => j.studyId === folderCheck ).length !== 0
    
    return status
  }, [localConfig, watchStudyName])

  useEffect(() => {
    if (!localStudyWarning) return

    toast(`A local run with the same name exists. 
      Try to change the study name or local run folder to avoid the overwrite.`, {
      duration: 8000,
      position: 'bottom-left',
      icon: '⚠️',
      style: { fontSize: '14px' },
      iconTheme: {
        primary: '#000',
        secondary: '#fff',
      },
      ariaProps: {
        role: 'alert',
        'aria-live': 'assertive',
      },
    })
  }, [localStudyWarning])

  /**
   * Show trigger for each input which has an alias at least
   * TODO: remove the DAGLinked condition for more control
   * @param item IInputMapping item
   * @returns trigger component
   */
  const addTriggerComponent = (item: IInputMapping) => {
    // clean error message if Linked alias
    triggers[item.index] && errors[item.name] 
      && item?.hasAlias === InputTypeAlias.DAGLinkedInputAlias 
      && clearErrors(item.name)

    const component = item.useAlias 
      ? (( item?.hasAlias === InputTypeAlias.DAGLinkedInputAlias ) && <ToggleOn 
        title='Link/unlink'
        style={{ 
          display: 'inline',
          float: 'right',
          color: '#1890ff',
          fontSize: '20px' }} 
        onClick={ () => updateTrigger(item.index) } />)
      : (( item?.hasAlias === InputTypeAlias.DAGLinkedInputAlias ) && <ToggleOff 
        title='Link/unlink'
        style={{ 
          display: 'inline',
          float: 'right',
          color: '#1890ff',
          fontSize: '20px' }} 
        onClick={ () => updateTrigger(item.index) } />)

      return component
    }

  return <form
    id={recipe.metadata.name}
    ref={submitRef}
    onSubmit={handleSubmit(_onSubmit, onErrors)}>

    {showForm && <div>
      <div className='cards-container-recipe' style={style}>
        {/* Title */}
        <div style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center' }}>
          <div>
            <span style={{ marginRight: '0.75rem' }}>
              <Avatar 
                src={recipe ? recipe.metadata.icon : ''} 
                size={24} />
            </span>
            <h5 style={{ display: 'inline-block', fontSize: '18px' }}>
              {recipe.metadata.name}
            </h5>
          </div>
          <div style={{ marginLeft: '0.75rem' }}>
            {children}
          </div>
          {host !== 'web' && 
            <SettingOutlined 
              title='Show settings'
              onMouseOver={() => setOverSettings(true)}
              onMouseLeave={() => setOverSettings(false)}
              onClick={() => setDisplaySettings(prev => !prev)}
              style={{ 
                fontSize: '18px',
                cursor: 'pointer',
                marginLeft: 'auto',
                color: `${(overSettings) 
                  ? '#40a9ff' : '#000'}`
              }}
            />}
        </div>

        
        <div className='cards-recipe-header'>
          {/* Study name */}
          <FormInput
            name='study-name'
            description={'Study name is a required field.'}
            errorMessage={errors['study-name'] ? errors['study-name']?.message : undefined}
          >
            <TextInput
              inputProps={{
                ...register('study-name'),
                defaultValue: studyName ?? `${recipe.metadata.name} study`,
              }}
              icon={localStudyWarning ? <ExclamationTriangle /> : undefined}
              reset={() => handleReset({ name: 'study-name', 
                default: `${recipe.metadata.name} study` })}
            />
          </FormInput>
            
          {/* Study description */}
          <FormInput
            name='study-description'
            description={'Study description is a optional field.'}
            errorMessage={errors['studydescription'] ? errors['study-description']?.message : undefined}
          >
            <TextInput
              inputProps={{
                ...register('study-description'),
                defaultValue: '',
              }}
              asTextArea = {true}
              reset={() => handleReset({ name: 'study-description', 
                default: '' })}
            />
          </FormInput>

          {/* Handlers - LOCAL ONLY */}
          {host !== 'web' && displaySettings && <FormInput
            name='load-input-aliases'
            description={'Enable handlers for an additional validation and/or to convert inputs to objects that are needed by the recipe.'}
          >
            <Switch.Root className='SwitchRoot' 
              id='po-material-switch'
              checked={handlers}
              onClick={(e: any) => {
                setHandlers(prev => !prev)
              }}
              >
              <Switch.Thumb className='SwitchThumb' />
            </Switch.Root>
          </FormInput>}
        </div>
      </div>

      {/* Dynamic fields by recipe */}
      <Tabs forceRenderTabPanel={true}>
        <TabList
          style={{
            flex: 1,
            margin: '10px 0 0 0',
          }}
        >
          {hasCustomTabs && <Tab key={'Recipe Inputs'}>{'Recipe Inputs'}</Tab>}
          {hasCustomTabs && customTabs.map((c) => <Tab key={c}>{c}</Tab>)}

          {!hasCustomTabs && <Tab key='required-tab'>Required inputs</Tab>}
          {!hasCustomTabs && <Tab key='optional-tab'>Optional inputs</Tab>}
        </TabList>
        {/* Required Inputs */}
        {!hasCustomTabs && <TabPanel key={'required-tab'}>
          <div className='cards-container-recipe' style={{ ...style, padding: '20px' }}>
            {/* Required */}
            <div className='cards-recipe'>
            {inputs && client && inputs.filter(i => i.required).map(i => {
              if (controlledValues && controlledValues[i.name]) {
                return <div key={`div-${i.name}`}>{setControlledInputs(i)}<Tag><GearWideConnected style={{ margin: '0 5px 0 0' }} />{i.name}</Tag></div>
              }
              return ( 
                <div key={`div-${i.name}`}>
                  { i.hasAlias && addTriggerComponent(i) }
                  <FormInput
                    key={`${i.name}-${i}`}
                    name={i.name}
                    description={i.description}
                    errorMessage={errors[i.name] ? errors[i.name]?.message : undefined}>
                      { formComponents(i) }
                  </FormInput>
                </div>)
              })}
            </div>
          </div>
        </TabPanel>}
        {/* Optional Inputs */}
        {!hasCustomTabs && 
        <TabPanel key={'optional-tab'}>
          <div className='cards-container-recipe' style={{ ...style, padding: '20px' }}>
            {/* Optional */}
            <div className='cards-recipe'>
            {inputs && client && inputs.filter(i => !i.required).map(i => {
              if (controlledValues && controlledValues[i.name]) {
                return <div key={`div-${i.name}`}>{setControlledInputs(i)}<Tag><GearWideConnected style={{ margin: '0 5px 0 0' }} />{i.name}</Tag></div>
              }
              return ( 
                <div key={`div-${i.name}`}>
                  { i.hasAlias && addTriggerComponent(i) }
                  <FormInput
                    key={`${i.name}-${i}`}
                    name={i.name}
                    description={i.description}
                    errorMessage={errors[i.name] ? errors[i.name]?.message : undefined}>
                      { formComponents(i) }
                  </FormInput>
                </div>)
            })}
            </div>
          </div>
        </TabPanel>}
        {/* Base input if custom tabs */}
        {hasCustomTabs && 
        <TabPanel key={'Recipe Inputs'}>
          <div className='cards-container-recipe' style={{ ...style, padding: '20px' }}>
            {/* Optional */}
            <div className='cards-recipe'>
            {inputs && client && inputs.filter(i => i.customTab == null).map(i => {
              if (controlledValues && controlledValues[i.name]) {
                return <div key={`div-${i.name}`}>{setControlledInputs(i)}<Tag><GearWideConnected style={{ margin: '0 5px 0 0' }} />{i.name}</Tag></div>
              }
              return ( 
                <div key={`div-${i.name}`}>
                  { i.hasAlias && addTriggerComponent(i) }
                  <FormInput
                    key={`${i.name}-${i}`}
                    name={i.name}
                    description={i.description}
                    errorMessage={errors[i.name] ? errors[i.name]?.message : undefined}>
                      { formComponents(i) }
                  </FormInput>
                </div>)
            })}
            </div>
          </div>
        </TabPanel>}
        {/* Custom tabs */}
        {hasCustomTabs && customTabs.map((c) => <TabPanel key={c}>
          <div className='cards-container-recipe' style={{ ...style, padding: '20px' }}>
            {/* Custom tab */}
            <div className='cards-recipe'>
            {inputs && client && inputs.filter(i => i.customTab === c).map(i => {
              if (controlledValues && controlledValues[i.name]) {
                return <div key={`div-${i.name}`}>{setControlledInputs(i)}<Tag><GearWideConnected style={{ margin: '0 5px 0 0' }} />{i.name}</Tag></div>
              }
              return ( 
                <div key={`div-${i.name}`}>
                  { i.hasAlias && addTriggerComponent(i) }
                  <FormInput
                    key={`${i.name}-${i}`}
                    name={i.name}
                    description={i.description}
                    errorMessage={errors[i.name] ? errors[i.name]?.message : undefined}>
                      { formComponents(i) }
                  </FormInput>
                </div>)
            })}
            </div>
          </div>
        </TabPanel>)}
      </Tabs>
    </div>}
    
    <div style={{ margin: '10px 0 0 0' }}>
      {/*Errors */}
      {host !== 'web' &&
        <ConfigureLocalRun
          onChange={(localConfig) => setLocalConfig(localConfig)} 
          defaultVal={localConfig}
        />
      }
      <Toaster/>
      
      {host === 'web' && <Button type='submit'
        style={{ width: '100%',
          margin: '10px 0 0 0',
          justifyContent: 'center' }}
        disabled={!isValid || loading}
        form={recipe.metadata.name}
        loading={loading}>
        Create Study
      </Button>}
      {/* Count error for validation because of triggers */}
      {host !== 'web' && <Button type='submit'
        style={{ width: '100%',
          margin: '10px 0 0 0',
          justifyContent: 'center' }}
        disabled={!localConfig.isLocalJob 
          ? (Object.keys(errors).length !== 0) || loading 
          : (Object.keys(errors).length !== 0) || loading || localConfig.localRunFolder == null}
        form={recipe.metadata.name}
        loading={loading}>
        Create Study
      </Button>}
      {!showForm && (Object.keys(errors).length !== 0) && inputs.map(i => errors[i.name] && <Tag style={{ color:'red',
        backgroundColor: 'rgba(255, 0, 0, 0.1)', 
        border: '1px solid red',
        margin: '8px'
        }}>
        {errors[i.name].message}
      </Tag>)}
    </div>
  </form>
}

export default RecipeForm
